// Generated by GitHub Copilot
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Smartphone } from 'lucide-react'
import { useComponentState } from '#core/state/State.ts'
import { useUserState } from '#core/state/UserState.ts'
import { AuthenticatorAppState } from './authenticator-app-state.ts'
import { AuthenticatorAppSetup } from './authenticator-app-setup.tsx'
import { AuthenticatorAppRemoveDialog } from './authenticator-app-remove-dialog.tsx'
import { AuthenticatorAppNotice } from './authenticator-app-notice.tsx'

/** Card showing whether the user has an authenticator app, with the setup flow and the remove action. */
export function AuthenticatorAppSection({ id }: { id?: string }) {
  const { t } = useTranslation('settings')
  const state = useComponentState(AuthenticatorAppState)
  const userState = useUserState()
  const [removing, setRemoving] = useState(false)
  const app = state.factors?.factorOf('authenticator_app') ?? null
  const canRemove = app?.canBeRemovedIn(userState.user?.login_method ?? null) ?? false
  const baseId = useId()
  const titleId = `${baseId}-title`
  const hintId = `${baseId}-remove-hint`

  return (
    <section id={id} className="card" aria-labelledby={titleId}>
      <div className="card-body">
        <div className="d-flex align-items-start gap-3">
          <Smartphone size={24} className="text-primary flex-shrink-0 mt-1" aria-hidden="true" />
          <div className="flex-grow-1">
            <h2 id={titleId} className="h6 fw-bold mb-1">{t('security.authenticator.title')}</h2>
            <p className="text-secondary mb-2">{t('security.authenticator.description')}</p>
            {app?.confirmed ? (
              <span className="badge bg-success bg-opacity-10 text-success">
                {t('security.authenticator.statusOn', { date: app.formattedConfirmedAt })}
              </span>
            ) : state.factors ? (
              <span className="badge bg-secondary bg-opacity-10 text-secondary">{t('security.authenticator.statusOff')}</span>
            ) : !state.loadFailed && (
              <span className="text-secondary small">{t('shared.common.loading')}</span>
            )}
          </div>
        </div>

        {state.loadFailed && <div className="alert alert-danger mt-3 mb-0" role="alert">{t('security.authenticator.loadError')}</div>}

        {state.unlockedCompanies && (
          <div className="alert alert-success mt-3 mb-0" role="status">
            {state.unlockedCompanies.length > 0
              ? t('security.authenticator.success.unlocked', { companies: state.unlockedCompanies.join(', ') })
              : t('security.authenticator.success.done')}
          </div>
        )}

        {state.enrollment ? (
          <AuthenticatorAppSetup state={state} enrollment={state.enrollment} />
        ) : app?.confirmed ? (
          <div className="mt-3">
            <button
              type="button"
              className="btn btn-outline-danger"
              disabled={!canRemove}
              aria-describedby={canRemove ? undefined : hintId}
              onClick={() => setRemoving(true)}
            >
              {t('security.authenticator.remove')}
            </button>
            {!canRemove && <p id={hintId} className="text-secondary small mt-2 mb-0">{t('security.authenticator.removeNeedsCode')}</p>}
          </div>
        ) : state.factors && (
          <div className="mt-3">
            <AuthenticatorAppNotice notice={state.notice} />
            <button type="button" className="btn btn-primary" disabled={state.busy} onClick={() => void state.startSetup()}>
              {t('security.authenticator.setUp')}
            </button>
          </div>
        )}
      </div>

      <AuthenticatorAppRemoveDialog isOpen={removing} state={state} onClose={() => setRemoving(false)} />
    </section>
  )
}
